<script lang="ts" setup>
type LocationPage = 'locations' | 'materials';

interface Props {
  locationIds: string[];
  searchQuery: string;
  visibleItemsCount: number;
  itemType: SegmentKey;
  page: LocationPage;
}
const { t } = useEkI18n();
const emit = defineEmits<{
  (e: 'open-wholesaler-modal'| 'open-location-modal', item: MaterialItemApp): void;

}>();
const props = defineProps<Props>();
const locationItemByLocationIdStore = useLocationItemByLocationIdStore();
const { _isFinalPage, _isFinalPageLocationPage } = storeToRefs(locationItemByLocationIdStore);
const locationItems = computed(() => props.page === 'materials' ? (locationItemByLocationIdStore._getLocationItemByLocationId ?? []) : (locationItemByLocationIdStore._getLocationItemByLocationIdLocationPage ?? []));
const { window, windowSize, incrementCount, resetWindow } = useArrayWindow(locationItems);
const pageNumber = ref(1);
const isSearching = ref(false);
const hideOutOfStockRef = ref(false);

async function loadLocationItems(append: boolean = false) {
  const request: LocationItemForLocationIdsRequest = {
    LocationIds: props.locationIds,
    PageSize: incrementCount.value,
    PageNumber: pageNumber.value,
    SearchQuery: props.searchQuery,
    HideOutOfStock: hideOutOfStockRef.value
  };

  let items: MaterialItemApp[] | undefined = [];

  if (props.page === 'materials') {
    items = await locationItemByLocationIdStore.setLocationItemByLocationId(request, append);
  } else {
    items = await locationItemByLocationIdStore.setLocationItemByLocationIdLocationPage(request, append);
  }

  if (items?.length === 1 && props.searchQuery !== '') {
    emit('open-location-modal', items[0]);
  }
}

function hideOutOfStockChanged(hideOutOfStock: boolean) {
  hideOutOfStockRef.value = hideOutOfStock;
}

async function loadMoreData(event: Event) {
  if ((props.page === 'materials' ? locationItemByLocationIdStore._isFinalPage === true : locationItemByLocationIdStore._isFinalPageLocationPage === true) || isSearching.value === true) {
    event.preventDefault();

    return;
  }

  isSearching.value = true;
  windowSize.value += incrementCount.value;
  pageNumber.value += 1;

  await loadLocationItems(true);
  isSearching.value = false;

  (event.target as HTMLIonInfiniteScrollElement).complete();
}

async function reset() {
  await showLoading(t('loading.loading'));
  locationItemByLocationIdStore._getLocationItemByLocationId = [];
  resetWindow();
  _isFinalPage.value = true;
  pageNumber.value = 1;
  await loadLocationItems();
  _isFinalPage.value = false;
  await hideLoading();
}

async function resetItemsLocationPage() {
  await showLoading(t('loading.loading'));
  locationItemByLocationIdStore._getLocationItemByLocationIdLocationPage = [];
  resetWindow();
  _isFinalPageLocationPage.value = true;
  pageNumber.value = 1;
  await loadLocationItems();
  _isFinalPageLocationPage.value = false;
  await hideLoading();
}

watch(() => props.searchQuery, async (newQuery, oldQuery) => {
  if (newQuery !== oldQuery) {
    if (props.page === 'materials') {
      await reset();
    } else {
      await resetItemsLocationPage();
    }
  }
});
defineExpose({ reset, resetItemsLocationPage, hideOutOfStockChanged });
const disableLoading = computed(() => props.page === 'materials' ? locationItemByLocationIdStore._isFinalPage : locationItemByLocationIdStore._isFinalPageLocationPage);
</script>

<template>
  <IonList>
    <MaterialItemLookUp
      v-for="item in window"
      :key="item.RecordId"
      :item="item"
      :item-type="itemType"
      :show-favorite-button="false"
      @open-wholesaler-modal="emit('open-wholesaler-modal', item);"
      @open-location-modal="emit('open-location-modal', item);"
    />
    <InputInfiniteScroll
      v-model="windowSize"
      :disabled="disableLoading"
      :increment-count="incrementCount"
      @ion-infinite="loadMoreData"
    >
      <IonInfiniteScrollContent :loading-text="t('loading.loading')" />
    </InputInfiniteScroll>
    <div v-if="!locationItems.length">
      <UserMessagesSearch
        :header="t('search.noResults', {item: t('materials.item', 2).toLowerCase()})"
        icon="fa-light fa-pallet-boxes"
        :subheader="t('search.noResultsText')"
      />
    </div>
  </IonList>
</template>

<style scoped>

</style>